import { useQuery } from '@tanstack/react-query'
import { ethers } from 'ethers'
import { getReadProvider } from './useProvider'
import { CHALLENGE_ABI } from '../abis/ChallengeABI'

// Public Sepolia RPC accepts up to ~50k-block ranges per eth_getLogs; 40k keeps a safe margin.
const BATCH_SIZE = 40_000
const LOOK_BACK_BLOCKS = 100_000

export interface ProofInfo {
  participant: string
  round: number
  cidHash: string
  isEliminated: boolean
  proofStatus: 'valid' | 'review' | 'invalid'
  submittedAt: number
}

interface ReportItem {
  reporter: string
  target: string
  round: number
  status: string
}

async function fetchProofsByRound(challengeAddress: string, round: number): Promise<ProofInfo[]> {
  const provider = getReadProvider()
  const contract = new ethers.Contract(challengeAddress, CHALLENGE_ABI, provider)

  const currentBlock = await provider.getBlockNumber()
  const fromBlock = Math.max(0, currentBlock - LOOK_BACK_BLOCKS)

  // Fetch the log ranges concurrently instead of one sequential round-trip per 2k blocks.
  const ranges: Array<[number, number]> = []
  for (let from = fromBlock; from <= currentBlock; from += BATCH_SIZE) {
    ranges.push([from, Math.min(from + BATCH_SIZE - 1, currentBlock)])
  }
  const batches = await Promise.all(
    ranges.map(
      (r) =>
        contract.queryFilter(contract.filters.ProofSubmitted(null), r[0], r[1]) as Promise<
          ethers.EventLog[]
        >,
    ),
  )

  const rawProofs: Array<{
    participant: string
    round: number
    cidHash: string
    blockNumber: number
  }> = []
  for (const log of batches.flat()) {
    const ev = log as ethers.EventLog
    if (Number(ev.args.round) === round) {
      rawProofs.push({
        participant: (ev.args.participant as string).toLowerCase(),
        round: Number(ev.args.round),
        cidHash: ev.args.cidHash as string,
        blockNumber: ev.blockNumber,
      })
    }
  }

  if (rawProofs.length === 0) return []

  // Deduplicated block timestamp fetch
  const uniqueBlocks = [...new Set(rawProofs.map((p) => p.blockNumber))]
  const blockTimestamps = new Map<number, number>()
  await Promise.all(
    uniqueBlocks.map(async (blockNum) => {
      const block = await provider.getBlock(blockNum)
      if (block) blockTimestamps.set(blockNum, block.timestamp)
    }),
  )

  // Check eliminated status for each participant
  const eliminatedResults = await Promise.all(
    rawProofs.map((p) => contract.eliminated(p.participant) as Promise<boolean>),
  )

  // Fetch reports for review status
  const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ''
  let reports: ReportItem[] = []
  try {
    const resp = await fetch(`${apiBase}/api/reports?challenge=${challengeAddress}`)
    if (resp.ok) {
      const data = (await resp.json()) as { reports?: ReportItem[] }
      reports = data.reports ?? []
    }
  } catch {
    // non-fatal
  }

  return rawProofs.map((p, i) => {
    const isEliminated = eliminatedResults[i]
    const hasActiveReport = reports.some(
      (r) =>
        r.target.toLowerCase() === p.participant && r.round === round && r.status === 'pending',
    )
    let proofStatus: 'valid' | 'review' | 'invalid' = 'valid'
    if (isEliminated) proofStatus = 'invalid'
    else if (hasActiveReport) proofStatus = 'review'

    return {
      participant: p.participant,
      round: p.round,
      cidHash: p.cidHash,
      isEliminated,
      proofStatus,
      submittedAt: blockTimestamps.get(p.blockNumber) ?? 0,
    }
  })
}

export function useProofsByRound(challengeAddress: string, round: number) {
  return useQuery<ProofInfo[]>({
    queryKey: ['proofs', challengeAddress, round],
    queryFn: () => fetchProofsByRound(challengeAddress, round),
    enabled: Boolean(challengeAddress),
    staleTime: 30_000,
  })
}
